import React, {
  memo, useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import {
  KeyMomentCategory,
  CardColor,
  DEFAULT_KEY_MOMENT_LABELS,
} from './types';
import {
  getHalfLabel,
  getCategoryLabel,
  getKeyMomentPrimaryLabel,
  shouldHideKeyMomentClock,
  getKeyMomentUiModifier,
  isPenaltyMissMomentCode,
  truncateKeyMomentSubstitutionPlayerName,
} from './utils';
import KeyMomentWhistleIcon from './KeyMomentWhistleIcon';
import KeyMomentsTeamLogoImg from './KeyMomentsTeamLogoImg';
import { keyMomentRowTouchesUnavailable } from './mergeKeyMomentsSameMinute';

const CARD_SELECTOR = '[data-incidence-id]';
const SCROLL_DURATION = 220;
const MAX_SCROLL_DURATION = 320;
const SCROLL_PIXELS_PER_MS = 1.35;

function easeOutCubic(progress) {
  return 1 - ((1 - progress) ** 3);
}

function getAdaptiveDuration(distance, minimumDuration) {
  const distanceBasedDuration = Math.abs(distance) / SCROLL_PIXELS_PER_MS;
  return Math.min(MAX_SCROLL_DURATION, Math.max(minimumDuration, distanceBasedDuration));
}

function KeyMomentCard({
  item,
  isSelected,
  isUnavailable,
  onClick,
  labels,
}) {
  const { formatMessage } = useIntl();
  const handleClick = useCallback(() => onClick(item), [onClick, item]);

  const modifier = getKeyMomentUiModifier(item);
  const halfLabel = getHalfLabel(item.half);
  const primaryLabel = getKeyMomentPrimaryLabel(item, labels, formatMessage);
  const categoryLabel = getCategoryLabel(item.category, item.cardColor, labels, item.moment);
  const hideClock = shouldHideKeyMomentClock(item);
  const penaltyMiss = isPenaltyMissMomentCode(item.moment);
  const eventOnlyLogos = !item.teamUrl && item.iconUrl;
  const isMergedMultiCard = item.category === KeyMomentCategory.CARD
    && (item.mergedSegments?.length > 1);
  let substitutionSegments = [];
  if (item.category === KeyMomentCategory.SUBSTITUTION) {
    substitutionSegments = item.mergedSegments?.length > 0
      ? item.mergedSegments
      : [{
        incidenceId: item.incidenceId,
        playerName: item.playerName,
        playerNameIn: item.playerNameIn,
      }];
  }

  if (item.category === KeyMomentCategory.MATCH_STATUS) {
    return (
      <button
        type="button"
        className={[
          'dtv-common-c-key-moments__card',
          'dtv-common-c-key-moments__card--match_status',
          'dtv-common-c-key-moments__card--status-milestone',
          isSelected && 'dtv-common-c-key-moments__card--selected',
          isUnavailable && 'dtv-common-c-key-moments__card--unavailable',
          isSelected && 'dtv-common-c-key-moments__card--controller-focused',
        ].filter(Boolean).join(' ')}
        onClick={handleClick}
        aria-pressed={isSelected}
        aria-label={primaryLabel}
        tabIndex={-1}
      >
        <span className="dtv-common-c-key-moments__card-status-row">
          <KeyMomentWhistleIcon className="dtv-common-c-key-moments__card-status-whistle" item={item} />
          <span className="dtv-common-c-key-moments__card-status-label">{primaryLabel}</span>
        </span>
      </button>
    );
  }

  const teamLogoSlot = !eventOnlyLogos ? (
    <KeyMomentsTeamLogoImg
      teamUrl={item.teamUrl}
      className="dtv-common-c-key-moments__card-team-logo"
    />
  ) : null;

  let cardFooter = null;
  if (item.category === KeyMomentCategory.SUBSTITUTION && isSelected) {
    cardFooter = (
      <div className="dtv-common-c-key-moments__card-substitution-players">
        {substitutionSegments.map((seg) => (
          <div
            key={seg.incidenceId}
            className="dtv-common-c-key-moments__card-substitution-row"
          >
            <div className="dtv-common-c-key-moments__card-substitution-player dtv-common-c-key-moments__card-substitution-player--out">
              <span
                className="dtv-common-c-key-moments__card-substitution-icon-box dtv-common-c-key-moments__card-substitution-icon-box--out"
                aria-hidden="true"
              >
                <span className="dtv-common-c-key-moments__card-substitution-icon dtv-common-c-key-moments__card-substitution-icon--out">▼</span>
              </span>
              <span
                className="dtv-common-c-key-moments__card-substitution-name dtv-common-c-key-moments__card-substitution-name--out"
                title={seg.playerName || undefined}
              >
                {seg.playerName
                  ? truncateKeyMomentSubstitutionPlayerName(seg.playerName)
                  : '–'}
              </span>
            </div>
            <div className="dtv-common-c-key-moments__card-substitution-player dtv-common-c-key-moments__card-substitution-player--in">
              <span
                className="dtv-common-c-key-moments__card-substitution-icon-box dtv-common-c-key-moments__card-substitution-icon-box--in"
                aria-hidden="true"
              >
                <span className="dtv-common-c-key-moments__card-substitution-icon dtv-common-c-key-moments__card-substitution-icon--in">▲</span>
              </span>
              <span
                className="dtv-common-c-key-moments__card-substitution-name dtv-common-c-key-moments__card-substitution-name--in"
                title={seg.playerNameIn || undefined}
              >
                {seg.playerNameIn
                  ? truncateKeyMomentSubstitutionPlayerName(seg.playerNameIn)
                  : '–'}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (isMergedMultiCard && isSelected) {
    cardFooter = (
      <div className="dtv-common-c-key-moments__card-merged-card-names">
        {item.mergedSegments.map((seg) => (
          seg.playerName ? (
            <span
              key={seg.incidenceId}
              className="dtv-common-c-key-moments__card-merged-card-name"
              title={seg.playerName}
            >
              {truncateKeyMomentSubstitutionPlayerName(seg.playerName)}
            </span>
          ) : null
        ))}
      </div>
    );
  } else if (item.category !== KeyMomentCategory.SUBSTITUTION) {
    const cardCollapsedNames = item.category === KeyMomentCategory.CARD
      && item.mergedSegments?.length > 0
      ? item.mergedSegments
        .map((s) => truncateKeyMomentSubstitutionPlayerName(s.playerName || ''))
        .filter(Boolean)
        .join(' · ')
      : item.playerName;
    if (cardCollapsedNames) {
      cardFooter = (
        <div className="dtv-common-c-key-moments__card-player">
          {cardCollapsedNames}
        </div>
      );
    }
  }

  return (
    <button
      type="button"
      className={[
        'dtv-common-c-key-moments__card',
        isSelected && 'dtv-common-c-key-moments__card--selected',
        isUnavailable && 'dtv-common-c-key-moments__card--unavailable',
        `dtv-common-c-key-moments__card--${modifier}`,
        isSelected && 'dtv-common-c-key-moments__card--controller-focused',
        isSelected && `dtv-common-c-key-moments__card--${modifier}--controller-focused`,
      ].filter(Boolean).join(' ')}
      onClick={handleClick}
      aria-pressed={isSelected}
      aria-label={`${primaryLabel} ${item.minutes}' ${halfLabel} ${item.playerName || ''}`}
      tabIndex={-1}
    >
      <div className="dtv-common-c-key-moments__card-header">
        <div
          className={[
            'dtv-common-c-key-moments__card-logos',
            eventOnlyLogos && 'dtv-common-c-key-moments__card-logos--event-only',
          ].filter(Boolean).join(' ')}
        >
          {teamLogoSlot}
          {item.iconUrl && (
            <img
              className="dtv-common-c-key-moments__card-icon"
              src={item.iconUrl}
              alt=""
              loading="lazy"
            />
          )}
          {penaltyMiss && (item.teamUrl || item.iconUrl) && (
            <span className="dtv-common-c-key-moments__card-penalty-miss-mark" aria-hidden="true">
              ×
            </span>
          )}
        </div>
        <div>
          {hideClock && item.category === KeyMomentCategory.GOAL ? (
            <div
              className={[
                'dtv-common-c-key-moments__card-body',
                'dtv-common-c-key-moments__card-body--shootout-row',
              ].join(' ')}
            >
              <span
                className={[
                  'dtv-common-c-key-moments__card-category',
                  `dtv-common-c-key-moments__card-category--${modifier}`,
                ].join(' ')}
              >
                {penaltyMiss
                  ? `${primaryLabel}${item.score ? ` · ${item.score}` : ''}`
                  : `${categoryLabel}${item.score ? ` · ${item.score}` : ''}`}
              </span>
            </div>
          ) : (
            <>
              {!hideClock && (
                <span className="dtv-common-c-key-moments__card-time">
                  {`${item.minutes}'`}
                  <span className="dtv-common-c-key-moments__card-time-divider" aria-hidden="true" />
                  {halfLabel}
                </span>
              )}
              {!hideClock && (
                <div className="dtv-common-c-key-moments__card-body">
                  <span
                    className={[
                      'dtv-common-c-key-moments__card-category',
                      `dtv-common-c-key-moments__card-category--${modifier}`,
                    ].join(' ')}
                  >
                    {penaltyMiss || item.category === KeyMomentCategory.MATCH_STATUS
                      ? primaryLabel
                      : categoryLabel}
                  </span>
                  {item.score && penaltyMiss ? (
                    <span className="dtv-common-c-key-moments__card-score">
                      {`\u2006· ${item.score}`}
                    </span>
                  ) : null}
                  {item.score && !penaltyMiss ? (
                    <span className="dtv-common-c-key-moments__card-score">
                      {item.score}
                    </span>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {cardFooter}
    </button>
  );
}

KeyMomentCard.propTypes = {
  item: PropTypes.shape({
    incidenceId: PropTypes.number,
    moment: PropTypes.string,
    category: PropTypes.string,
    cardColor: PropTypes.oneOf(Object.values(CardColor)),
    half: PropTypes.number,
    minutes: PropTypes.number,
    teamUrl: PropTypes.string,
    iconUrl: PropTypes.string,
    playerName: PropTypes.string,
    playerNameIn: PropTypes.string,
    score: PropTypes.string,
    mergedSegments: PropTypes.arrayOf(PropTypes.shape({
      incidenceId: PropTypes.number,
      moment: PropTypes.string,
      cardColor: PropTypes.string,
      playerName: PropTypes.string,
      playerNameIn: PropTypes.string,
    })),
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  isUnavailable: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  labels: PropTypes.shape({}).isRequired,
};

const MemoKeyMomentCard = memo(KeyMomentCard);

/**
 * Horizontal carousel centered on `selectedId`. Click → `onSelect` (caller seeks).
 * Immersive mode: ‹ › steps selection via `onCenterChange`; user scroll drag is disabled.
 */
function KeyMomentsCarousel({
  immersive,
  items,
  selectedId,
  unavailableIds,
  labels,
  onCarouselDismiss,
  onCenterChange,
  onSelect,
}) {
  const { formatMessage } = useIntl();
  const scrollRef = useRef(null);
  const animationFrameRef = useRef();
  const prefersReducedMotionRef = useRef(false);
  const [sideSpacerWidth, setSideSpacerWidth] = useState();

  const cancelScrollAnimation = useCallback(() => {
    if (animationFrameRef.current === undefined) return;
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = undefined;
  }, []);

  const animateScrollTo = useCallback((nextScrollLeft, duration) => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const targetScrollLeft = Math.max(0, nextScrollLeft);
    const startScrollLeft = scrollElement.scrollLeft;
    const distance = targetScrollLeft - startScrollLeft;
    const adaptiveDuration = getAdaptiveDuration(distance, duration);

    cancelScrollAnimation();

    if (duration <= 0 || prefersReducedMotionRef.current || Math.abs(distance) < 1) {
      scrollElement.scrollLeft = targetScrollLeft;
      return;
    }

    const animationStartTime = performance.now();
    const step = (currentTime) => {
      const elapsed = currentTime - animationStartTime;
      const progress = Math.min(1, elapsed / adaptiveDuration);
      const eased = easeOutCubic(progress);

      scrollElement.scrollLeft = startScrollLeft + (distance * eased);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
        return;
      }
      animationFrameRef.current = undefined;
    };

    animationFrameRef.current = requestAnimationFrame(step);
  }, [cancelScrollAnimation]);

  const updateSideSpacerWidth = useCallback(() => {
    const scrollElement = scrollRef.current;
    const firstCard = scrollElement?.querySelector(CARD_SELECTOR);
    if (!scrollElement || !firstCard) {
      // eslint-disable-next-line unicorn/no-useless-undefined
      setSideSpacerWidth(undefined);
      return;
    }
    setSideSpacerWidth(Math.max(0, (scrollElement.clientWidth - firstCard.offsetWidth) / 2));
  }, []);

  const scrollCardIntoCenter = useCallback((element, duration) => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !element) return;
    const nextScrollLeft = element.offsetLeft
      - ((scrollElement.clientWidth - element.offsetWidth) / 2);
    animateScrollTo(nextScrollLeft, duration);
  }, [animateScrollTo]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => {};
    }
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => {
      prefersReducedMotionRef.current = mediaQuery.matches;
    };
    updateMotionPreference();
    mediaQuery.addEventListener?.('change', updateMotionPreference);
    return () => {
      mediaQuery.removeEventListener?.('change', updateMotionPreference);
    };
  }, []);

  useEffect(() => () => cancelScrollAnimation(), [cancelScrollAnimation]);

  useLayoutEffect(() => {
    updateSideSpacerWidth();
  }, [items.length, updateSideSpacerWidth]);

  useEffect(() => {
    if (items.length === 0) return () => {};

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSideSpacerWidth);
      return () => window.removeEventListener('resize', updateSideSpacerWidth);
    }

    const scrollElement = scrollRef.current;
    const firstCard = scrollElement?.querySelector(CARD_SELECTOR);
    const observer = new ResizeObserver(() => updateSideSpacerWidth());

    if (scrollElement) observer.observe(scrollElement);
    if (firstCard) observer.observe(firstCard);

    return () => observer.disconnect();
  }, [items.length, updateSideSpacerWidth]);

  // Recentralize whenever selectedId changes (and the spacer is computed).
  useLayoutEffect(() => {
    if (!scrollRef.current || items.length === 0 || sideSpacerWidth === undefined) return;
    if (selectedId === undefined) return;

    const target = scrollRef.current.querySelector(`[data-incidence-id="${selectedId}"]`);
    if (target) scrollCardIntoCenter(target, SCROLL_DURATION);
  }, [selectedId, items.length, sideSpacerWidth, scrollCardIntoCenter]);

  const stepAdjacentSelection = useCallback((direction) => {
    if (!onCenterChange || items.length === 0) return;
    const fromId = items.findIndex((i) => i.incidenceId === selectedId);
    const base = fromId >= 0 ? fromId : 0;
    const next = Math.min(items.length - 1, Math.max(0, base + direction));
    if (next !== base) {
      onCenterChange(next);
    }
  }, [items, onCenterChange, selectedId]);

  useEffect(() => {
    if (!immersive || items.length === 0) return () => {};

    const typingTarget = () => {
      const el = document.activeElement;
      const tag = el?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
      if (el?.isContentEditable) return true;
      return false;
    };

    const handleKeyDown = (e) => {
      if (typingTarget()) return;

      if (e.key === 'Escape') {
        if (onCarouselDismiss) {
          e.preventDefault();
          e.stopPropagation();
          onCarouselDismiss();
        }
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        stepAdjacentSelection(-1);
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        stepAdjacentSelection(1);
        return;
      }

      if (e.key === 'Enter') {
        const idx = items.findIndex((i) => i.incidenceId === selectedId);
        const i = idx >= 0 ? idx : 0;
        const chosen = items[i];
        if (!chosen || keyMomentRowTouchesUnavailable(chosen, unavailableIds)) return;
        e.preventDefault();
        onSelect?.(chosen);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [
    immersive,
    items,
    selectedId,
    unavailableIds,
    onCarouselDismiss,
    onSelect,
    stepAdjacentSelection,
  ]);

  const handleCardClick = useCallback((item) => {
    if (keyMomentRowTouchesUnavailable(item, unavailableIds)) return;
    onSelect?.(item);
  }, [onSelect, unavailableIds]);

  if (items.length === 0) return null;

  const scrollPrevLabel = formatMessage({
    id: 'keyMoments.carouselPrev',
    defaultMessage: 'Previous moment',
  });
  const scrollNextLabel = formatMessage({
    id: 'keyMoments.carouselNext',
    defaultMessage: 'Next moment',
  });
  const hintsNavigate = formatMessage({
    id: 'keyMoments.immersive.hintsNavigate',
    defaultMessage: '< > + ENTER',
  });
  const hintsExit = formatMessage({
    id: 'keyMoments.immersive.hintsExit',
    defaultMessage: 'EXIT (ESC)',
  });

  const track = (
    <div
      ref={scrollRef}
      className={
        immersive
          ? 'dtv-common-c-key-moments__carousel-track dtv-common-c-key-moments__carousel-track--immersive'
          : 'dtv-common-c-key-moments__carousel-track'
      }
      aria-label="Key moments"
    >
      <div
        aria-hidden="true"
        className="dtv-common-c-key-moments__carousel-spacer"
        style={{ width: sideSpacerWidth ?? 0 }}
      />
      {items.map((item) => {
        const isUnavailable = keyMomentRowTouchesUnavailable(item, unavailableIds);
        const isSelected = item.incidenceId === selectedId;
        return (
          <div
            key={item.incidenceId}
            data-incidence-id={item.incidenceId}
            className="dtv-common-c-key-moments__card-wrapper"
          >
            {isUnavailable && (
            <span
              className="dtv-common-c-key-moments__card-unavailable-label"
              aria-hidden="true"
            >
              {labels.unavailable}
            </span>
            )}
            <MemoKeyMomentCard
              item={item}
              isSelected={isSelected}
              isUnavailable={isUnavailable}
              onClick={handleCardClick}
              labels={labels}
            />
          </div>
        );
      })}
      <div
        aria-hidden="true"
        className="dtv-common-c-key-moments__carousel-spacer"
        style={{ width: sideSpacerWidth ?? 0 }}
      />
    </div>
  );

  if (!immersive) {
    return (
      <div className="dtv-common-c-key-moments__carousel">
        {track}
      </div>
    );
  }

  const navIndex = (() => {
    const idx = items.findIndex((i) => i.incidenceId === selectedId);
    return idx >= 0 ? idx : 0;
  })();
  const atCarouselStart = navIndex <= 0;
  const atCarouselEnd = navIndex >= items.length - 1;

  return (
    <div className="dtv-common-c-key-moments__immersive-frame">
      <div className="dtv-common-c-key-moments__keyboard-hints" aria-hidden="true">
        <span className="dtv-common-c-key-moments__keyboard-hints-cluster">
          <span className="dtv-common-c-key-moments__keyboard-hints-nav">{hintsNavigate}</span>
          <span className="dtv-common-c-key-moments__keyboard-hints-exit">{hintsExit}</span>
        </span>
      </div>
      <div className="dtv-common-c-key-moments__carousel dtv-common-c-key-moments__carousel--immersive">
        <button
          type="button"
          className="dtv-common-c-key-moments__carousel-nav dtv-common-c-key-moments__carousel-nav--prev"
          aria-label={scrollPrevLabel}
          disabled={atCarouselStart}
          onClick={() => stepAdjacentSelection(-1)}
        >
          <span className="dtv-icon-arrow-back" />
        </button>
        {track}
        <button
          type="button"
          className="dtv-common-c-key-moments__carousel-nav dtv-common-c-key-moments__carousel-nav--next"
          aria-label={scrollNextLabel}
          disabled={atCarouselEnd}
          onClick={() => stepAdjacentSelection(1)}
        >
          <span className="dtv-icon-arrow-foward" />
        </button>
      </div>
    </div>
  );
}

KeyMomentsCarousel.propTypes = {
  immersive: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    incidenceId: PropTypes.number.isRequired,
  })).isRequired,
  selectedId: PropTypes.number,
  unavailableIds: PropTypes.instanceOf(Set),
  labels: PropTypes.shape({
    unavailable: PropTypes.string,
  }),
  onCarouselDismiss: PropTypes.func,
  onCenterChange: PropTypes.func,
  onSelect: PropTypes.func,
};

KeyMomentsCarousel.defaultProps = {
  immersive: false,
  selectedId: undefined,
  unavailableIds: undefined,
  labels: DEFAULT_KEY_MOMENT_LABELS,
  onCarouselDismiss: undefined,
  onCenterChange: undefined,
  onSelect: undefined,
};

export default memo(KeyMomentsCarousel);
